// A URL of this app pasted on its own into a body becomes a reference, the way
// the server normalizes a bare URL when it saves.
//
// Promise:
// - Only a URL alone (spaces around it allowed) of this app's own origin is
//   taken, and only when its path is one a part registered (`FavoriteKind.refPaths`).
//   Anything else is an ordinary paste.
// - The pasted text goes in at once, as text. When the reference resolves and
//   that text is still where it was put, it is replaced by the reference node;
//   otherwise (it changed, it is not visible, the request failed) it simply stays
//   text. Nothing typed meanwhile is lost.
import type { QueryClient } from "@tanstack/react-query";
import type { EditorView } from "@tiptap/pm/view";
import { matchRefPath, refPathOrder } from "@trameria/core/refs-shared";
import type { FavoriteKind } from "../app/screen-part";
import type { Api } from "../data/api";
import { fetchResolvedRef, type RefRequest } from "./queries";

/** The origins whose URLs name things of this app: the page's own and the desktop scheme. */
export function appOrigins(): string[] {
  const own = typeof location === "undefined" ? null : location.origin;
  return [...(own && own !== "null" ? [own] : []), "trameria://app"];
}

const decode = (raw: string): string => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/** Every kind's paths in the order the server tries them. */
function orderedPaths(
  kinds: Readonly<Record<string, FavoriteKind>>,
): { kind: string; pattern: string }[] {
  return Object.entries(kinds)
    .flatMap(([kind, view]) =>
      (view.refPaths ?? []).map((pattern) => ({ kind, pattern })),
    )
    .sort((a, b) => refPathOrder(a.pattern) - refPathOrder(b.pattern));
}

/** The reference a pasted text names, or null when it is not one URL of this app. */
export function refRequestFromText(
  text: string,
  kinds: Readonly<Record<string, FavoriteKind>>,
  origins: readonly string[] = appOrigins(),
): RefRequest | null {
  const url = text.trim();
  if (url === "" || /\s/.test(url)) return null;
  let rest: string | null = null;
  for (const origin of origins) {
    const base = origin.replace(/\/+$/, "");
    if (url.startsWith(`${base}/`)) {
      rest = url.slice(base.length);
      break;
    }
  }
  if (rest === null) return null;
  const seg = rest.split(/[?#]/)[0].split("/").filter(Boolean).map(decode);
  for (const { kind, pattern } of orderedPaths(kinds)) {
    const named = matchRefPath(pattern, seg);
    if (named) return { kind, ...named };
  }
  return null;
}

/**
 * For `editorProps.handlePaste`: takes the paste when it is one URL of this app
 * (true), else leaves it to the editor (false).
 */
export function handleRefPaste(
  view: EditorView,
  event: ClipboardEvent,
  context: {
    kinds: Readonly<Record<string, FavoriteKind>>;
    qc: QueryClient;
    api: Api;
  },
): boolean {
  const text = event.clipboardData?.getData("text/plain") ?? "";
  const req = refRequestFromText(text, context.kinds);
  if (!req) return false;
  event.preventDefault();
  const pasted = text.trim();
  const { from, to } = view.state.selection;
  view.dispatch(view.state.tr.insertText(pasted, from, to));
  const end = from + pasted.length;
  void fetchResolvedRef(context.qc, context.api, req)
    .then((ref) => {
      if (!ref.visible || view.isDestroyed) return;
      const { state } = view;
      if (end > state.doc.content.size) return;
      if (state.doc.textBetween(from, end) !== pasted) return;
      const node = state.schema.nodes.trameriaLink?.create({
        kind: ref.kind,
        refId: ref.id,
        label: ref.label,
      });
      if (node) view.dispatch(state.tr.replaceRangeWith(from, end, node));
    })
    .catch(() => {
      // The text is already there; it stays text.
    });
  return true;
}
